import {
  PropsWithChildren,
  ReactElement,
  useId,
  useState,
  ComponentProps,
} from 'react';

import { IonList, IonAccordion, IonAccordionGroup } from '@ionic/react';
import classNames from 'classnames';
import { useTheme } from 'styled-components';

import { ThemePaletteColor } from '../../types';

import { SectionHeader } from './SectionHeader/SectionHeader';
import { SectionTitle } from './SectionTitle/SectionTitle';
import { SectionContent } from './SectionContent/SectionContent';
import { SectionCollapse } from './SectionCollapse/SectionCollapse';
import { SectionContext, SectionContexState } from './Section.context';

import styles from './Section.module.scss';

export type SectionProps = PropsWithChildren<{
  className?: string;
  background?: ThemePaletteColor;
}>;

interface SectionComponent {
  (props: SectionProps): ReactElement<any, any> | null;
  Header: typeof SectionHeader;
  Title: typeof SectionTitle;
  Content: typeof SectionContent;
  Collapse: typeof SectionCollapse;
}

type OnChange = ComponentProps<typeof IonAccordionGroup>['onIonChange'];

export const Section: SectionComponent = ({
  className,
  children,
  background = 'white100',
}) => {
  const id = useId();
  const { palette } = useTheme();

  const [state, setState] = useState<SectionContexState>({
    collapsible: false,
    value: null,
  });

  const handleOnChange: OnChange = ({ detail: { value } }) => {
    setState((prev) => ({ ...prev, value }));
  };

  return (
    <SectionContext.Provider value={{ ...state, setState }}>
      {state.collapsible ? (
        <IonAccordionGroup className={styles.root} onIonChange={handleOnChange}>
          <IonAccordion
            value={id}
            style={{ background: palette[background] }}
            className={classNames(className)}
          >
            {children}
          </IonAccordion>
        </IonAccordionGroup>
      ) : (
        <IonList
          style={{ background: palette[background] }}
          className={classNames(styles.root, styles.list, className)}
        >
          {children}
        </IonList>
      )}
    </SectionContext.Provider>
  );
};

Section.Header = SectionHeader;
Section.Title = SectionTitle;
Section.Content = SectionContent;
Section.Collapse = SectionCollapse;
